<template>
	<div>
		<base-header class="pb-6 bg-light">
			<b-row align-v="center" class="py-4">
				<b-col cols="12" lg="6">
					<h6 class="h2 d-inline-block mb-0 text-dark">TAP Roamers</h6>
				</b-col>
				<b-col cols="12" lg="6" class="text-right">
					<nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
						<route-bread-crumb></route-bread-crumb>
					</nav>
				</b-col>
			</b-row>
		</base-header>
		<b-row class="m-2">
			<b-col cols="auto">
				<b-card>
					<b-form @submit.prevent="submitForm">
						<b-col sm="12">
							<div class="mt-2">
								<label for="msisdn" class="font-weight-900 text-sm">IMSI</label> &nbsp;
								<small>(Optional)</small>
								<b-form-input
									type="number"
									id="imsi"
									placeholder="Enter IMSI"
									v-model="imsi"
									:state="validateImsi"
								></b-form-input>
								<b-form-invalid-feedback :state="validateImsi">
									Please enter a valid IMSI (15 digits)
								</b-form-invalid-feedback>
							</div>

							<div class="mt-2">
								<label for="period" class="font-weight-900 text-sm">Period</label>
								<b-form-select
									id="period"
									v-model="period"
									:options="periodOption"
									value-field="key"
									text-field="value"
									@change="getSelectedDate(period)"
								></b-form-select>
							</div>
							<div class="mt-2" v-if="period === 'daily'">
								<label for="selectDay" class="col-form-label form-control-label">Select Day:</label>
								<base-input type="date" v-model="dynamicDate" id="selectDay" @change="selectDate()" />
							</div>

							<div class="mt-2" v-if="period === 'weekly'">
								<label for="selectWeek" class="col-form-label form-control-label">Select Week:</label>
								<base-input type="week" v-model="dynamicDate" id="selectWeek" @change="selectWeek()" />
							</div>

							<div class="mt-2" v-if="period === 'monthly'">
								<label for="selectMonth" class="col-form-label form-control-label">Select Month:</label>
								<b-form-select
									id="selectMonth"
									v-model="dynamicDate"
									:options="monthOption"
									value-field="key"
									text-field="value"
									@change="selectMonth()"
								></b-form-select>
							</div>
							<!-- <hr class="mt-3 mb-3" /> -->
							<div class="text-right mt-3">
								<base-button
									type="primary"
									title="Search"
									size="sm"
									@click="submitForm"
									:loading="loadingStatus"
									:disabled="submitDisabled"
									class="mb-3"
								>
									Update Map
								</base-button>
							</div>
						</b-col>
					</b-form>
				</b-card>
			</b-col>
			<b-col class="bg-light p-1" style="height: 610px" v-if="mapLoading">
				<!-- Data Volume By Region Card (map) -->
				<div class="text-center w-100">
					<p v-if="mapLoading">Searching for location data on IMSI {{ imsi }}</p>
					<b-icon
						icon="three-dots"
						class="text-center"
						animation="cylon"
						font-scale="4"
						v-if="mapLoading"
					></b-icon>
				</div>
			</b-col>
			<b-col class="bg-light p-1" style="height: 610px" :class="{'d-none': mapLoading}">
				<div id="map" class="map-canvas" style="height: 100%"></div>
			</b-col>
		</b-row>
		<b-row>
			<b-card class="w-100" id="locationTable">
				<b-card-header>
					<b-row>
						<b-col cols="auto" class="d-flex justify-content-center align-items-center">
							<b-form-group
								label=""
								label-for="per-page-select"
								label-cols-sm="0"
								label-cols-md="0"
								label-cols-lg="0"
								label-align-sm="center"
								label-size="sm"
								class="mb-2 per-page-select"
							>
								<label for="per-page-select" class="mr-2 text-sm mt-1 mr-4">Show</label>
								<b-form-select
									id="per-page-select"
									class="diameter-report-select"
									v-model="perPage"
									:options="pageOptions"
									size="sm"
									v-on:input="changePageOption(perPage)"
								></b-form-select>
							</b-form-group>
						</b-col>
						<b-col class="d-flex justify-content-center align-items-center">
							<b-dropdown size="sm" variant="primary" toggle-class="text-decoration-none" menu-class="card-body p-0 m-0">
								<template #button-content> Export Table Data </template>
								<b-dropdown-item @click="exportData('csv')"> Export as CSV </b-dropdown-item>
								<b-dropdown-item @click="exportData('xls')"> Export as XLS </b-dropdown-item>
							</b-dropdown>
						</b-col>
						<b-col cols="auto" class="customer-table-select">
							<b-form-group
								label=""
								label-for="filter-input"
								label-align-sm="right"
								label-size="sm "
								class="mb-0 per-page-select"
							>
								<label for="filter-input" class="text-sm mr-2 mt-1">Search:</label>
								<b-input-group size="sm">
									<b-form-input id="filter-input2" v-model="filter" class="mb-4"></b-form-input>
								</b-input-group>
							</b-form-group>
						</b-col>
					</b-row>
				</b-card-header>
				<b-card-body>
					<b-table
						:items="items"
						:fields="columns"
						:current-page="currentPage"
						:per-page="perPage"
						:filter="filter"
						:filter-included-fields="filterOn"
						:sort-by.sync="sortBy"
						:sort-desc.sync="sortDesc"
						:sort-direction="sortDirection"
						table-class="text-nowrap"
						class="primary-table"
						sticky-header
						thead-class="white"
						show-empty
						responsive
						small
					>
						<template #cell(imsi)="row">
							<a
								role="button"
								@click="handleImsiClick(row.item)"
								title="Click on an IMSI to see it displayed on the map"
								style="cursor: pointer"
							>
								<i class="fas fa-map-marker-alt"></i>
								{{ row.item.imsi }}
							</a>
						</template>

						<template #empty="scope">
							<span v-if="loadingStatus">
								<b-icon icon="three-dots" class="text-center" animation="cylon" font-scale="4"></b-icon>
							</span>
							<b v-else>{{ scope.emptyText }}</b>
						</template>
						<template #emptyfiltered="scope">
							<h4>{{ scope.emptyFilteredText }}</h4>
						</template>
					</b-table>

					<b-row v-if="items.length > 0" class="mt-4">
						<b-col>
							<p class="ml-3 nowrap">{{ startPage() }} - {{ lastPage() }} of {{ totalRows }}</p>
						</b-col>
						<b-col>
							<b-pagination
								class="flex-fill-custom pr-4 ml-3 justify-content-end"
								v-model="currentPage"
								:total-rows="totalRows"
								:per-page="perPage"
								size="sm"
							></b-pagination>
						</b-col>
					</b-row>
				</b-card-body>
			</b-card>
		</b-row>
	</div>
</template>

<script>
import routeBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import moment from "moment";
import pagination from "../../../common/format/pagination";
import {exportToCSV, exportToExcel} from "@/common/export-to-csv";
import administratorService from "../../../common/api-services/administrator-service";
import configDirective from "../../../common/directive/config-directive";

export default {
	name: "tap-roamers",
	components: {
		routeBreadCrumb,
	},
	mounted() {
		document.title = "TAP Roamers";
		this.startDate = moment().subtract(1, "days").format("YYYY-MM-DD");
		this.endDate = moment().format("YYYY-MM-DD");

		this.loadMap(this.map);
	},
	data() {
		return {
			startDate: "",
			endDate: "",
			imsi: "",
			msisdn: "",
			operator: "",
			location: "",
			period: "daily",
			selectDayStatus: true,
			selectWeekStatus: false,
			selectMonthStatus: false,
			dynamicDate: "",
			monthOption: [],
			periodOption: [
				// {key: "1h", value: "Last 1 hours"},
				// {key: "6h", value: "Last 6 hours"},
				// {key: "24h", value: "Last 24 hours"},
				{key: "daily", value: "Daily"},
				{key: "weekly", value: "Weekly"},
				{key: "monthly", value: "Monthly"},
			],
			items: [
				// {
				// 	date: "2023-10-01",
				// 	imsi: "123456789012345",
				// 	lat_lon: "64.8943634,-162.5948882",
				// 	data: "1500",
				// 	connectiontype: "LTE",
				// 	operator: "Verizon",
				// 	weight: 1,
				// },
				// {
				// 	date: "2023-10-01",
				// 	imsi: "123456789012654",
				// 	lat_lon: "66.8943634,-164.5948882",
				// 	data: "1000",
				// 	connectiontype: "3G",
				// 	operator: "Verizon",
				// 	weight: 10,
				// },
			],
			columns: [
				{key: "date", label: "Date", sortable: true},
				{key: "imsi", label: "IMSI", sortable: true},
				{key: "lat_lon", label: "Lat Lon", sortable: true},
				{key: "data", label: "Data Used", sortable: true},
				{key: "operator", label: "Operator", sortable: true},
			],
			totalRows: 0,
			currentPage: 1,
			perPage: 10,
			fromPage: 10,
			pageOptions: [10, 25, 50, 100],
			sortBy: "date",
			sortDesc: true,
			sortDirection: "desc",
			filter: null,
			filterOn: [],
			loadingStatus: false,
			mapLoading: false,
			map: null,
			markerList: [],
			heatmapLayer: [],
			startingCenter: null,
			startingZoom: null,
		};
	},
	computed: {
		submitDisabled() {
			return !this.validateDates && this.validateImsi !== false;
		},
		validateDates() {
			if (this.startDate && this.endDate) {
				return this.startDate < this.endDate;
			}
			return null;
		},
		validateImsi() {
			if (this.imsi.length > 0) {
				return this.imsi.length === 15;
			}
			return null;
		},
		validateMsisdn() {
			if (this.msisdn.length > 0) {
				return this.msisdn.length === 11;
			}
			return null;
		},
	},
	methods: {
		submitForm() {
			// Handle form submission here
			console.log("Start Date:", this.startDate);
			console.log("End Date:", this.endDate);
			console.log("IMSI:", this.imsi);
			let data = {
				start_date: this.startDate,
				end_date: this.endDate,
				imsi: this.imsi,
				// msisdn: this.msisdn,
			};
			administratorService
				.getTapRoamers(data)
				.then(response => {
					console.log(response);
					// this.items = response.data;
					// this.totalRows = response.data.length;
				})
				.catch(error => {
					console.error(error);
				})
				.finally(() => {
					this.loadingStatus = false;
					this.loadMap(this.map);
				});
		},

		exportData(format) {
			let fileName = `tap-roamers-${moment(this.startDate).format("MM-D-YYYY")}`;
			let load = structuredClone(this.items);
			load.forEach(item => {
				item["Data Used"] = this.formatUsageData(item.data);
				item["Connection Type"] = item.connectiontype;
				item["Operator"] = item.operator;
				item["Session Count"] = item.weight;
				item["LAT/LON"] = item.lat_lon.replace(",", " ");
				delete item.data;
				delete item.connectiontype;
				delete item.operator;
				delete item.weight;
				delete item.lat_lon;
			});
			if (format === "csv") {
				exportToCSV(load, fileName);
			} else if (format === "xls") {
				exportToExcel(load, fileName);
			}
		},

		handleImsiClick(data) {
			// console.log("data:", data);
			// console.log("MAP:", this.map.zoom);
			this.map.panTo(new google.maps.LatLng(data.lat_lon.split(",")[0], data.lat_lon.split(",")[1], true));
			this.map.setZoom(9);
			this.markerList.forEach(marker => {
				let lat = marker.getPosition().lat();
				let lng = marker.getPosition().lng();

				if (marker.title === data.imsi) {
					marker.setAnimation(google.maps.Animation.DROP);
					let infowindow = new google.maps.InfoWindow({
						content:
							"<div> " +
							'<p style="font-size: 12px;"><span style="font: bold 12px Roboto, serif; color: #34495e;">IMSI: </span> ' +
							data.imsi +
							'<br><p style="font-size: 12px;"><span style="font: bold 12px Roboto, serif; color: #34495e;">Location: </span> </b> ' +
							data.lat_lon +
							'<p style="font-size: 12px;"><span style="font: bold 12px Roboto, serif; color: #34495e;">Connection Type: </span> ' +
							data.connectiontype +
							'<p style="font-size: 12px;"><span style="font: bold 12px Roboto, serif; color: #34495e;">Data Used: </span> ' +
							this.formatUsageData(data.data) +
							'<p style="font-size: 12px;"><span style="font: bold 12px Roboto, serif; color: #34495e;">Operator: </span> ' +
							data.operator +
							"</div>",
					});
					setTimeout(() => {
						infowindow.open(this.map, marker);
					}, 1000);

					marker.addListener("click", function () {
						infowindow.open(this.map, marker);
					});

					// close infowindow when zoom < 15
					google.maps.event.addListener(this.map, "zoom_changed", function () {
						// if (this.map.getZoom() < 15) {
						infowindow.close();
						// }
					});

					// close infowindow when click on map
					google.maps.event.addListener(this.map, "click", function () {
						infowindow.close();
					});
				} else if (data.lat_lon === lat + ", " + lng) {
					// console.log("NO MATCH:", lat, lng);
					marker.setMap(null);
				}
			});
		},

		loadMap(map) {
			this.mapLoading = true;
			try {
				let configData = configDirective.getConfigData();
				let bound = new google.maps.LatLngBounds();

				let map_center = configData["site-settings"]["map_center"].split(",");
				let center = new google.maps.LatLng(map_center[0], map_center[1], true);
				let zoom = configData["site-settings"]["map_zoom"] ? configData["site-settings"]["map_zoom"] : 3;

				map = new google.maps.Map(document.getElementById("map"), {
					center: center,
					zoom: zoom,
					mapTypeId: google.maps.MapTypeId.ROADMAP,
				});

				if (this.items.length > 0) {
					for (let i in this.items) {
						let value = this.items[i];
						if (value != null) {
							this.addMarker(value, map, bound);
							this.heatmapLayer.push({
								location: new google.maps.LatLng(
									value.lat_lon.split(",")[0],
									value.lat_lon.split(",")[1],
									true
								),
								weight: value.data,
							});
						}
					}
				} else {
					let default_value = {
						date: moment().format("YYYY-MM-DD"),
						imsi: "NULL",
						lat_lon: map_center[0] + "," + map_center[1],
						data: "NULL",
						connectiontype: "NULL",
						operator: "NULL",
					};
					let defaultMarker = this.addMarker(default_value, map, bound);
          defaultMarker.setAnimation(google.maps.Animation.DROP);
          setTimeout(() => {
            let infowindow = new google.maps.InfoWindow({
              content:
                '<div> ' +
                '<p style="margin-top: 15px; font: bold 12px Roboto, serif; color: #34495e;"> No Data Found </p>' +
                "</div>",
            });
            infowindow.open(this.map, defaultMarker);
          }, 1000);
				}

        let heatmap = null;
				if (this.heatmapLayer.length > 1) {
					if (this.markerList) {
						this.markerList.forEach(marker => {
							marker.setMap(null);
						});
					}

          heatmap = new google.maps.visualization.HeatmapLayer({
            data: this.heatmapLayer,
            map: map,
          });
          heatmap.setMap(map);
          heatmap.set("radius", 20);
  
          // Calculate the center of the heatmap data
          let latSum = 0;
          let lonSum = 0;
          this.heatmapLayer.forEach(dataPoint => {
            latSum += dataPoint.location.lat();
            lonSum += dataPoint.location.lng();
          });
          const dataCenter = new google.maps.LatLng(
            latSum / this.heatmapLayer.length,
            lonSum / this.heatmapLayer.length
          );
  
          this.startingCenter = dataCenter;
          console.log("dataCenter: ", dataCenter);
				}

				// Set map center and initial zoom
				map.setCenter(this.startingCenter);
				map.setZoom(8);

				// Create "Center Map" button and add it to the map
				const centerControlDiv = document.createElement("div");
				const centerControl = this.createCenterControl(map, bound);

				centerControlDiv.appendChild(centerControl);
				map.controls[google.maps.ControlPosition.TOP_CENTER].push(centerControlDiv);

				// Define max and min zoom levels
				const maxZoom = 15;
				const minZoomToShowMarkers = 8;

				google.maps.event.addListener(map, "zoom_changed", () => {
					const currentZoom = map.getZoom();

					if (currentZoom <= minZoomToShowMarkers) {
						// Remove markers and show only heatmap
						if (heatmap) {
							heatmap.setMap(map);
						}
						if (this.markerList) {
							this.markerList.forEach(marker => {
								marker.setMap(null);
							});
						}
					} else {
						// Show markers and heatmap
						if (heatmap) {
							heatmap.setMap(null);
						}
						if (this.markerList) {
							this.markerList.forEach(marker => {
								marker.setMap(map);
							});
						}
					}

					// Limit zoom level
					if (currentZoom > maxZoom) {
						map.setZoom(maxZoom);
					}
				});

				// Set map center and zoom to fit all markers
				google.maps.event.addListener(map, "zoom_changed", function () {
					const zoomChangeBoundsListener = google.maps.event.addListener(
						map,
						"bounds_changed",
						function (event) {
							if (this.getZoom() > 15 && this.initialZoom == true) {
								this.setZoom(15);
								this.initialZoom = false;
							}
							google.maps.event.removeListener(zoomChangeBoundsListener);
						}
					);
				});

				map.initialZoom = true;
				map.fitBounds(bound);
				this.map = map;
			} catch (error) {
				console.error("CAUGHT-MAP: ", error);
			} finally {
				this.mapLoading = false;
			}
		},

		addMarker(data, map, bound) {
			console.log("MARKER:", data);

			let sel_icon = "https://i.imgur.com/2eE096C.png";
			if (data.connectiontype == "3G") {
				//green triangle
				sel_icon = "https://i.imgur.com/vSpGkBd.png";
			} else if (data.connectiontype == "LTE") {
				//green square
				sel_icon = "https://i.imgur.com/8jLOZnY.png";
			}

			let lat_lon = data.lat_lon.split(",");
			let lat = lat_lon[0];
			let lon = lat_lon[1];
			let latlng = new google.maps.LatLng(lat, lon);
			bound.extend(latlng);
			let marker = new google.maps.Marker({
				position: {
					lat: parseFloat(lat),
					lng: parseFloat(lon),
				},
				map: map,
				icon: sel_icon,
				title: data.imsi,
			});

      let content = "<div> " +
					'<p style="font-size: 12px;"><span style="font: bold 12px Roboto, serif; color: #34495e;">IMSI: </span> ' +
					data.imsi +
					'<br><p style="font-size: 12px;"><span style="font: bold 12px Roboto, serif; color: #34495e;">Location: </span> </b> ' +
					data.lat_lon +
					'<p style="font-size: 12px;"><span style="font: bold 12px Roboto, serif; color: #34495e;">Connection Type: </span> ' +
					data.connectiontype +
					'<p style="font-size: 12px;"><span style="font: bold 12px Roboto, serif; color: #34495e;">Data Used: </span> ' +
					this.formatUsageData(data.data) +
					'<p style="font-size: 12px;"><span style="font: bold 12px Roboto, serif; color: #34495e;">Operator: </span> ' +
					data.operator +
					"</div>";
      if (data.imsi === "NULL") {
        content = '<div> ' +
          '<p style="margin-top: 15px; font: bold 12px Roboto, serif; color: #34495e;"> No Data Found </p>' +
          "</div>";
      }
			let infowindow = new google.maps.InfoWindow({
				content: content,
			});

      marker.addListener("click", function () {
				infowindow.open(map, marker);
			});
			// close infowindow when zoom < 15
			google.maps.event.addListener(map, "zoom_changed", function () {
				if (map.getZoom() < 15) {
					infowindow.close();
				}
			});
			// close infowindow when click on map
			google.maps.event.addListener(map, "click", function () {
				infowindow.close();
			});

			bound.extend(marker.position);
			map.fitBounds(bound);

			this.markerList.push(marker);
      return marker;
		},

		createCenterControl(map, bound) {
			const controlButton = document.createElement("button");

			// Set CSS for the control.
			controlButton.style.backgroundColor = "#fff";
			controlButton.style.border = "2px solid #fff";
			controlButton.style.borderRadius = "3px";
			controlButton.style.boxShadow = "0 2px 6px rgba(0,0,0,.3)";
			controlButton.style.color = "rgb(25,25,25)";
			controlButton.style.cursor = "pointer";
			controlButton.style.fontFamily = "Roboto,Arial,sans-serif";
			controlButton.style.fontSize = "16px";
			controlButton.style.lineHeight = "38px";
			controlButton.style.margin = "8px 0 22px";
			controlButton.style.padding = "0 5px";
			controlButton.style.textAlign = "center";

			controlButton.textContent = "Recenter";
			controlButton.title = "Click to recenter the map";
			controlButton.type = "button";

			// Setup the click event listeners: simply set the map to Chicago.
			controlButton.addEventListener("click", () => {
				map.setCenter(this.startingCenter);
				map.fitBounds(bound);
			});

			return controlButton;
		},

		formatUsageData(data) {
			if (data >= 1000) {
				return (data / 1000).toFixed(2) + " GB";
			} else {
				return data + " MB";
			}
		},

		getSelectedDate(option) {
			switch (option) {
				case "daily": {
					this.selectDayStatus = true;
					this.selectWeekStatus = false;
					this.selectMonthStatus = false;
					this.getCurrentDate();
					break;
				}
				case "weekly": {
					this.selectWeekStatus = true;
					this.selectDayStatus = false;
					this.selectMonthStatus = false;
					this.getCurrentWeek();
					break;
				}
				case "monthly": {
					this.selectMonthStatus = true;
					this.selectDayStatus = false;
					this.selectWeekStatus = false;
					this.getCurrentMonth();
					break;
				}
				default:
					this.selectMonthStatus = false;
					this.selectDayStatus = false;
					this.selectWeekStatus = false;
					break;
			}
		},

		getCurrentDate() {
			let date = new Date();
			let thisYear = date.getFullYear();
			let thisDate = date.getDate();
			let thisMonth = date.getMonth() + 1;
			if (thisDate < 10) {
				thisDate = "0" + thisDate;
			}
			if (thisMonth < 10) {
				thisMonth = "0" + thisMonth;
			}
			this.dynamicDate = thisYear + "-" + thisMonth + "-" + thisDate;
			this.selectDate();
		},

		getCurrentWeek() {
			let today = new Date();
			let year = today.getFullYear();
			let oneJan = new Date(year, 0, 1);
			let numberOfDays = Math.floor((today - oneJan) / (24 * 60 * 60 * 1000));
			let result = Math.floor((today.getDay() + 1 + numberOfDays) / 7);
			this.dynamicDate = year + "-W" + result;
			this.selectWeek();
		},

		getCurrentMonth() {
			this.monthOption = [];
			let monthNames = [
				"January",
				"February",
				"March",
				"April",
				"May",
				"June",
				"July",
				"August",
				"September",
				"October",
				"November",
				"December",
			];
			let today = new Date();
			let year = today.getFullYear();
			let month = today.getMonth() + 1;
			let i = 0;
			do {
				let key = (month > 9 ? "" : "0") + month + "/01/" + year;
				let value = monthNames[month - 1] + " " + year;
				this.monthOption.push({
					key: key,
					value: value,
				});
				if (month - 1 < 1) {
					month = 13;
				}
				if (month === 0) {
					month = 11;
					year--;
				} else {
					month--;
				}
				i++;
			} while (i < 12);
			// console.log(moment().date(1).format("MM/DD/YYYY"))
			this.dynamicDate = moment().date(1).format("MM/DD/YYYY");
			this.selectMonth();
		},

		getTimeRangeCondition(period) {
			let timeRangeCondition;
			switch (period) {
				case "24h": {
					timeRangeCondition = "time >= now() - " + this.period;
					break;
				}
				case "6h": {
					timeRangeCondition = "time >= now() - " + this.period;
					break;
				}
				case "1h": {
					timeRangeCondition = "time >= now() - " + this.period;
					break;
				}
				case "daily": {
					timeRangeCondition =
						"time >= '" + this.selectedDateFrom + "' AND time <= '" + this.selectedDateTo + "' ";
					break;
				}
				case "weekly": {
					timeRangeCondition =
						"time >= '" + this.selectedDateFrom + "' AND time <= '" + this.selectedDateTo + "' ";
					break;
				}
				case "monthly": {
					timeRangeCondition =
						"time >= '" + this.selectedDateFrom + "' AND time <= '" + this.selectedDateTo + "' ";
					break;
				}
				default:
					break;
			}
			return timeRangeCondition;
		},

		selectDate() {
			let firstDay = moment(this.dynamicDate).format("YYYY-MM-DDT00:00:00") + "Z";
			let lastDay = moment(this.dynamicDate).format("YYYY-MM-DDT23:59:00") + "Z";
			// console.log("FIRST:", firstDay);
			// console.log("LAST:", lastDay);
			this.from_dt = firstDay;
			this.to_dt = lastDay;
		},

		selectWeek() {
			// console.log("WEEK:", this.dynamicDate);
			let weekNum = this.dynamicDate.split("W")[1];
			let year = this.dynamicDate.split("-")[0].trim();

			let firstDay = moment().day("Monday").year(year).week(Number(weekNum)).format("YYYY-MM-DDT00:00:00") + "Z";
			let lastDay =
				moment()
					.day("Sunday")
					.year(year)
					.week(Number(weekNum) + 1)
					.format("YYYY-MM-DDT23:59:00") + "Z";
			// console.log("FIRST:", firstDay);
			// console.log("LAST:", lastDay);

			this.from_dt = firstDay;
			this.to_dt = lastDay;
		},

		selectMonth() {
			let date = new Date(this.dynamicDate);
			// console.log(date)
			let y = date.getFullYear();
			let m = date.getMonth();
			let firstDay = moment(new Date(y, m, 1)).format("YYYY-MM-DDT00:00:00") + "Z";
			let lastDay = moment(new Date(y, m + 1, 0)).format("YYYY-MM-DDT23:59:00") + "Z";
			// console.log("FIRST:", firstDay);
			// console.log("LAST:", lastDay);
			this.from_dt = firstDay;
			this.to_dt = lastDay;
		},

		getTotalRows() {
			if (this.perPage > this.totalRows) {
				this.fromPage = this.totalRows;
			} else {
				this.fromPage = this.perPage;
			}
		},

		changePageOption(page) {
			this.getTotalRows();
		},

		startPage() {
			return pagination.startPageFunc(this.currentPage, this.perPage);
		},

		lastPage() {
			return pagination.lastPageFunc(this.currentPage, this.perPage, this.totalRows);
		},
	},
};
</script>

<style>
/* Your CSS styles go here */
</style>
